import React, { createContext, useContext, useEffect, useState } from 'react';
import { AccountWithOutageInfo, PspsDetails } from '../types';
import { CrcMarker } from '../components/community-resource-center';
import {
  Maybe,
  OutageEventDetails,
  OutageLookupMultipleOption,
  OutageLookupResponse,
  OutageLookUpType,
  PlannedOutageEvent,
  PspsOutageZones,
  ServicePointOutageDetails,
} from '../../../__generated__/pge-types';
import { usePspsHandler } from '../hooks/usePspsHandler';
import { featureFlagsContext } from '../../../providers/FeatureFlagsProvider';
export enum OutageMarkerView {
  CustomerAffected = 'customerAffectedOutage',
  Grouped = 'groupedOutage',
  NoOutage = 'noOutage',
  SearchedLocation = 'searchedLocationOutage',
  Planned = 'plannedOutage',
  CRC = 'crc',
  PSPS = 'psps',
}

export interface MultiPremiseInfoProp {
  isMultiPremiseAccount: boolean;
  MultiPremiseList: Maybe<Array<OutageLookupMultipleOption>> | undefined;
  isMultiSelectModalOpen: boolean;
  selectedPremiseDetails: OutageLookupResponse | undefined;
  selectedPremiseEncryptedValue: string | undefined;
}

export interface SinglePremiseInfoProp {
  houseNumber: string;
  premiseDetails: ServicePointOutageDetails | undefined;
  encryptedAccountNumber?: string | undefined;
  encryptedServicePointList?: string[] | undefined;
  selectedPremiseEncryptedValue: string | undefined;
}

export interface AccountNotFoundInfoProp {
  accountNotFoundModalOpen: boolean;
  lookupType: OutageLookUpType;
  lookupValue: string | undefined;
}

interface OutageLeftPanelStateContextProps {
  multiPremiseInfo: MultiPremiseInfoProp | undefined;
  setMultiPremiseInfo: (data: MultiPremiseInfoProp | undefined) => void;
  singlePremiseInfo: SinglePremiseInfoProp | undefined;
  setSinglePremiseInfo: (data: SinglePremiseInfoProp | undefined) => void;
  accountNotFoundInfo: AccountNotFoundInfoProp | undefined;
  setAccountNotFoundInfo: (data: AccountNotFoundInfoProp | undefined) => void;
  panelOpen: boolean;
  setPanelOpen: (val: boolean) => void;
  reportOutageOpen: boolean;
  setReportOutageOpen: (val: boolean) => void;
  houseNumber: string;
  setHouseNumber: (value: string) => void;
  outageMarkerView: OutageMarkerView | undefined;
  setOutageMarkerView: (type?: OutageMarkerView | undefined) => void;
  selectedAccountForOutage: AccountWithOutageInfo | undefined;
  setSelectedAccountForOutage: (
    account?: AccountWithOutageInfo | undefined,
  ) => void;
  accountListWithOutageInfo: AccountWithOutageInfo[] | undefined;
  setAccountListWithOutageInfo: (
    account?: AccountWithOutageInfo[] | undefined,
  ) => void;
  selectedOutageInfo: OutageEventDetails | PlannedOutageEvent | undefined;
  setSelectedOutageInfo: (
    account?: OutageEventDetails | PlannedOutageEvent | undefined,
  ) => void;
  crcMarkerDetails: CrcMarker | undefined;
  setCrcMarkerDetails: (marker: CrcMarker | undefined) => void;
  pspsDetails: PspsDetails | undefined;
  setPspsDetails: (pspsDetails: PspsDetails) => void;
  isLookUpCompleted: boolean;
  setLookUpCompleted: (val: boolean) => void;
}

export const OutageLeftPanelStateContext = createContext<
  OutageLeftPanelStateContextProps | undefined
>(undefined);

export const OutageLeftPanelStateProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [panelOpen, setPanelOpen] = useState<boolean>(true);
  const [reportOutageOpen, setReportOutageOpen] = useState<boolean>(false);
  const [accountNotFoundInfo, setAccountNotFoundInfo] = useState<
    AccountNotFoundInfoProp | undefined
  >(undefined);

  const [houseNumber, setHouseNumber] = useState<string>('');
  const [multiPremiseInfo, setMultiPremiseInfo] = useState<
    MultiPremiseInfoProp | undefined
  >(undefined);
  const [singlePremiseInfo, setSinglePremiseInfo] = useState<
    SinglePremiseInfoProp | undefined
  >(undefined);
  const [outageMarkerView, setOutageMarkerView] = useState<
    OutageMarkerView | undefined
  >(undefined);
  const [selectedAccountForOutage, setSelectedAccountForOutage] = useState<
    AccountWithOutageInfo | undefined
  >(undefined);
  const [accountListWithOutageInfo, setAccountListWithOutageInfo] = useState<
    AccountWithOutageInfo[] | undefined
  >(undefined);
  const [selectedOutageInfo, setSelectedOutageInfo] = useState<
    OutageEventDetails | PlannedOutageEvent | undefined
  >(undefined);
  const [crcMarkerDetails, setCrcMarkerDetails] = useState<
    CrcMarker | undefined
  >(undefined);
  const [pspsDetails, setPspsDetails] = useState<PspsDetails | undefined>(
    undefined,
  );
  const [isLookUpCompleted, setLookUpCompleted] = useState<boolean>(false);
  const { getPspsStatus, matchedZones } = usePspsHandler();
  const { isOutagesPSPSEnabled, loading: featureFlagLoading } = useContext(
    featureFlagsContext,
  );

  useEffect(() => {
    if (
      outageMarkerView === undefined && // Check account psps only on account view
      matchedZones.length > 0 && //Wait for the psps data to load
      selectedAccountForOutage &&
      selectedAccountForOutage?.pspsOutage?.outageZone &&
      isOutagesPSPSEnabled
    ) {
      const status = getPspsStatus(
        selectedAccountForOutage?.pspsOutage?.outageZone,
      );

      if (status) {
        setPspsDetails(status);
      } else {
        setPspsDetails(undefined);
      }
    }
  }, [selectedAccountForOutage, matchedZones, isOutagesPSPSEnabled]);

  const value = React.useMemo(
    () => ({
      panelOpen,
      setPanelOpen,
      outageMarkerView,
      setOutageMarkerView,
      selectedAccountForOutage,
      setSelectedAccountForOutage,
      selectedOutageInfo,
      setSelectedOutageInfo,
      crcMarkerDetails,
      setCrcMarkerDetails,
      pspsDetails,
      setPspsDetails,
      reportOutageOpen,
      setReportOutageOpen,
      houseNumber,
      setHouseNumber,
      multiPremiseInfo,
      setMultiPremiseInfo,
      accountNotFoundInfo,
      setAccountNotFoundInfo,
      singlePremiseInfo,
      setSinglePremiseInfo,
      accountListWithOutageInfo,
      setAccountListWithOutageInfo,
      isLookUpCompleted,
      setLookUpCompleted,
    }),
    [
      panelOpen,
      outageMarkerView,
      selectedAccountForOutage,
      selectedOutageInfo,
      crcMarkerDetails,
      pspsDetails,
      reportOutageOpen,
      houseNumber,
      multiPremiseInfo,
      accountNotFoundInfo,
      singlePremiseInfo,
      accountListWithOutageInfo,
      isLookUpCompleted,
    ],
  );
  return (
    <OutageLeftPanelStateContext.Provider value={value}>
      {children}
    </OutageLeftPanelStateContext.Provider>
  );
};

export const useOutageLeftPanelState = (): OutageLeftPanelStateContextProps => {
  const ctx = useContext(OutageLeftPanelStateContext);
  if (!ctx) {
    throw new Error(
      'useOutageLeftPanelState must be used within OutageLeftPanelStateProvider',
    );
  }
  return ctx;
};
